/**
 * DockMate Socket Service
 * Handles real-time WebSocket connections with robust reconnection
 */

import { io } from 'socket.io-client';
import { authAPI } from './api';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:3002';
const HEARTBEAT_INTERVAL = 30000; // 30 seconds

let socket = null;
let eventHandlers = {};
let connectionStateListeners = [];
let heartbeatTimer = null;
let reconnectCallback = null;

// Connection states
export const CONNECTION_STATES = {
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  ERROR: 'error'
};

let currentState = CONNECTION_STATES.DISCONNECTED;

/**
 * Notify all connection state listeners
 */
function notifyStateChange(state) {
  currentState = state;
  connectionStateListeners.forEach(listener => listener(state));
}

/**
 * Start heartbeat to keep connection alive
 */
function startHeartbeat() {
  stopHeartbeat();
  heartbeatTimer = setInterval(() => {
    if (socket?.connected) {
      socket.emit('ping');
    }
  }, HEARTBEAT_INTERVAL);
}

/**
 * Stop heartbeat
 */
function stopHeartbeat() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

/**
 * Connect to the WebSocket server
 */
export function connect() {
  const token = authAPI.getToken();

  if (!token) {
    console.warn('No auth token available for socket connection');
    return null;
  }

  if (socket?.connected) {
    return socket;
  }

  // Clean up existing socket if any
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
  }

  notifyStateChange(CONNECTION_STATES.CONNECTING);

  socket = io(SOCKET_URL, {
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: Infinity, // Keep trying forever
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000, // Max 10 seconds between attempts
    timeout: 20000,
  });

  socket.on('connect', () => {
    console.log('⚓ Socket connected');
    notifyStateChange(CONNECTION_STATES.CONNECTED);
    startHeartbeat();

    // Call reconnect callback to refetch notifications
    if (reconnectCallback) {
      reconnectCallback();
    }
  });

  socket.on('disconnect', (reason) => {
    console.log('Socket disconnected:', reason);
    notifyStateChange(CONNECTION_STATES.DISCONNECTED);
    stopHeartbeat();
  });

  socket.on('connect_error', (error) => {
    console.error('Socket connection error:', error.message);
    notifyStateChange(CONNECTION_STATES.ERROR);
  });

  socket.on('reconnect_attempt', (attemptNumber) => {
    console.log(`Socket reconnection attempt ${attemptNumber}`);
    notifyStateChange(CONNECTION_STATES.CONNECTING);
  });

  socket.on('reconnect', (attemptNumber) => {
    console.log(`Socket reconnected after ${attemptNumber} attempts`);
    notifyStateChange(CONNECTION_STATES.CONNECTED);
    startHeartbeat();

    // Call reconnect callback to refetch notifications
    if (reconnectCallback) {
      reconnectCallback();
    }
  });

  socket.on('reconnect_failed', () => {
    console.error('Socket reconnection failed after all attempts');
    notifyStateChange(CONNECTION_STATES.ERROR);
  });

  // Re-attach any registered event handlers
  Object.entries(eventHandlers).forEach(([event, handlers]) => {
    handlers.forEach(handler => {
      socket.on(event, handler);
    });
  });

  return socket;
}

/**
 * Disconnect from the WebSocket server
 */
export function disconnect() {
  stopHeartbeat();
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
  eventHandlers = {};
  notifyStateChange(CONNECTION_STATES.DISCONNECTED);
}

/**
 * Get the socket instance
 */
export function getSocket() {
  return socket;
}

/**
 * Get current connection state
 */
export function getConnectionState() {
  return currentState;
}

/**
 * Check if socket is connected
 */
export function isConnected() {
  return socket?.connected || false;
}

/**
 * Subscribe to connection state changes
 */
export function onConnectionStateChange(listener) {
  connectionStateListeners.push(listener);
  // Immediately notify with current state
  listener(currentState);

  // Return unsubscribe function
  return () => {
    connectionStateListeners = connectionStateListeners.filter(l => l !== listener);
  };
}

/**
 * Set callback to be called on reconnect (for refetching data)
 */
export function setReconnectCallback(callback) {
  reconnectCallback = callback;
}

/**
 * Force reconnect - useful when automatic reconnection fails
 */
export function forceReconnect() {
  console.log('Force reconnecting socket...');
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
  return connect();
}

/**
 * Register an event handler
 */
export function on(event, handler) {
  if (!eventHandlers[event]) {
    eventHandlers[event] = [];
  }
  eventHandlers[event].push(handler);

  if (socket) {
    socket.on(event, handler);
  }

  // Return unsubscribe function
  return () => {
    off(event, handler);
  };
}

/**
 * Remove an event handler
 */
export function off(event, handler) {
  if (eventHandlers[event]) {
    eventHandlers[event] = eventHandlers[event].filter(h => h !== handler);
  }

  if (socket) {
    socket.off(event, handler);
  }
}

/**
 * Emit an event
 */
export function emit(event, data) {
  if (socket?.connected) {
    socket.emit(event, data);
  } else {
    console.warn('Socket not connected. Cannot emit:', event);
  }
}

// ======================
// Chat Events
// ======================

export function joinConversation(conversationId) {
  emit('join_conversation', conversationId);
}

export function leaveConversation(conversationId) {
  emit('leave_conversation', conversationId);
}

export function startTyping(conversationId) {
  emit('typing_start', { conversation_id: conversationId });
}

export function stopTyping(conversationId) {
  emit('typing_stop', { conversation_id: conversationId });
}

export function markMessagesRead(conversationId, messageIds) {
  emit('mark_read', { conversation_id: conversationId, message_ids: messageIds });
}

// ======================
// Helper Events
// ======================

export function toggleAvailability(available) {
  emit('toggle_availability', { available });
}

export function updateLocation(serviceId, lat, lng) {
  emit('location_update', { service_id: serviceId, lat, lng });
}

// ======================
// Event Constants
// ======================

export const EVENTS = {
  // Chat events
  NEW_MESSAGE: 'new_message',
  USER_TYPING: 'user_typing',
  USER_STOPPED_TYPING: 'user_stopped_typing',
  MESSAGES_READ: 'messages_read',

  // Request events
  NEW_REQUEST: 'new_request',
  REQUEST_UPDATED: 'request_updated',
  NEW_APPLICATION: 'new_application',
  HELPER_SELECTED: 'helper_selected',

  // Service events
  SERVICE_STARTED: 'service_started',
  SERVICE_COMPLETED: 'service_completed',
  HELPER_LOCATION: 'helper_location',

  // Availability events
  AVAILABILITY_UPDATED: 'availability_updated',

  // General notifications
  NOTIFICATION: 'notification',
};

export default {
  connect,
  disconnect,
  getSocket,
  getConnectionState,
  isConnected,
  onConnectionStateChange,
  setReconnectCallback,
  forceReconnect,
  on,
  off,
  emit,
  joinConversation,
  leaveConversation,
  startTyping,
  stopTyping,
  markMessagesRead,
  toggleAvailability,
  updateLocation,
  EVENTS,
  CONNECTION_STATES,
};
